import React from 'react'
import clsx from 'clsx'
import { Paperclip2 } from 'iconsax-react'
import { useTheme } from '@mui/material'

import NamedAvatar from '../../../../atoms/NamedAvatar'

import useStyles from './styles'

interface SelectableClientProps {
  client: APIUser
  onClick: CallableFunction
  observerId: string
  selectable?: boolean
}

const SelectableClient: React.FC<SelectableClientProps> = ({
  client,
  onClick,
  selectable,
  observerId
}) => {
  const theme = useTheme()

  const classes = useStyles()

  const [intersect, setIntersect] = React.useState(null)
  const [element, setElement] = React.useState(null)

  const mediaCount = React.useMemo(() => {
    if (!client?.media_ids) {
      return 0
    }

    return client.media_ids.length
  }, [client])

  const handleClick = React.useCallback(
    e => {
      e.stopPropagation()
      if (e.target.tagName === 'DIV') {
        onClick()
      }
    },
    [onClick]
  )

  // observer variable to set viewport and check if user is in it
  const observer = React.useRef(
    new IntersectionObserver(
      entries => {
        setIntersect(entries[0])
      },
      { root: document.querySelector(`#${observerId}`), threshold: 0 }
    )
  )

  // effect for watching and unwatching observer variable
  React.useEffect(() => {
    const currentElement = element
    const currentObserver = observer?.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    // clean up observer to stop watching
    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [element])

  return (
    <div className={classes.root} ref={setElement} onClick={handleClick}>
      <div className={classes.avatarHolder}>
        {/* @ts-ignore */}
        <NamedAvatar
          selectable={selectable}
          user={client}
          avatarClassName={classes.avatar}
          intersect={intersect}
          customName={
            client.deleted_at ? (
              <div className={clsx(classes.title, classes.faded)}>
                (Deleted Contact)
              </div>
            ) : null
          }
        />
      </div>

      <div className={classes.counts}>
        {mediaCount > 0 && (
          <div
            className={classes.count}
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            <span className='count'>{mediaCount}</span>

            <span className='icon'>
              <Paperclip2 size={theme.size.base} />
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default SelectableClient
